/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 21-07-2026
 *
 * Root-service voor de status en gating van de instructievideo. Het `dl-video`-eiland zet hier de
 * play-state; andere consumenten (zoals `dl-chat` en de emptystate in `dl-modelcanvas`) lezen die
 * reactief mee.
 *
 * Sinds golf 4e leest deze service zijn projectgegevens zelf uit de `SessieService` en logt hij zelf
 * op de opslagketen; de `initialiseer` vanuit AngularJS is daarmee vervallen. Dat een startende video
 * de simulatie sluit, staat als melding: de `SimulatieService` injecteert deze service al, dus de
 * omgekeerde injectie kan niet.
 */
import {computed, inject, Injectable, signal} from '@angular/core';
import {OpslagService} from '../app/model/opslag.service';
import {Melding} from '../lib/melding';
import {SessieService} from './sessie.service';

@Injectable({providedIn: 'root'})
export class VideoStatusService {

    /**
     * De sessie: bron van de projectvlag en het modelleerniveau van het project.
     */
    private readonly sessie = inject(SessieService);

    /**
     * De opslagketen, voor de logregels `play_video` en `cancel_video`.
     */
    private readonly opslag = inject(OpslagService);

    /**
     * Of er op dit moment een instructievideo speelt.
     */
    private readonly _speelt = signal(false);

    /**
     * Telt de verzoeken om de startvideo te spelen; het `dl-video`-eiland speelt hem bij elke
     * ophoging. Een teller en geen vlag, zodat twee verzoeken na elkaar ook twee keer tellen.
     */
    private readonly _startverzoek = signal(0);

    /**
     * Telt de verzoeken om de picker te sluiten; zie `_startverzoek`.
     */
    private readonly _sluitverzoek = signal(0);

    /**
     * Meldt dat de play-state gewijzigd is. De `SimulatieService` sluit op een startende video de
     * simulatie, zoals de AngularJS-hoofdcontroller dat tot golf 4e deed.
     */
    private readonly speeltMelding = new Melding<boolean>();

    /** De play-state, leesbaar voor alle consumenten. */
    readonly speelt = this._speelt.asReadonly();

    /** De verzoeken om de startvideo; zie `vraagStartvideo`. */
    readonly startverzoek = this._startverzoek.asReadonly();

    /** De verzoeken om de picker te sluiten; zie `cancel`. */
    readonly sluitverzoek = this._sluitverzoek.asReadonly();

    /** Het modelleerniveau van het project, voor de niveaubepaling in het eiland. */
    readonly projectniveau = computed(() => this.sessie.modelleerniveau());

    /** Mag de video getoond worden: projectvlag én projectniveau vanaf 20. */
    readonly magVideo = computed(() => this.sessie.heeftStartvideo() && this.projectniveau() >= 20);

    /**
     * Vraagt het `dl-video`-eiland de startvideo voor het huidige niveau te spelen. Zo hoeft de
     * aanroeper het eiland niet in de DOM te zoeken.
     */
    vraagStartvideo(): void {
        this._startverzoek.update(teller => teller + 1);
    }

    /**
     * Zet een eventueel spelende video uit, inclusief de cancel-logging, en sluit een open picker.
     */
    cancel(): void {
        if (this._speelt()) {
            this.directlog('cancel_video', {});
        }
        this.zetSpeelt(false);
        this._sluitverzoek.update(teller => teller + 1);
    }

    /**
     * Zet de play-state en meldt de wijziging.
     */
    zetSpeelt(speelt: boolean): void {
        if (this._speelt() === speelt) {
            return;
        }
        this._speelt.set(speelt);
        this.speeltMelding.meld(speelt);
    }

    /**
     * Abonneert op een wijziging van de play-state; geeft de afmeldfunctie terug.
     */
    bijSpeeltGewijzigd(luisteraar: (speelt: boolean) => void): () => void {
        return this.speeltMelding.abonneer(luisteraar);
    }

    /**
     * Logt een videoactie (`play_video`/`cancel_video`) rechtstreeks, zonder dat hij in de
     * actiebuffer van een save belandt — zoals de ng1-`directlogAction` dat deed.
     */
    directlog(actie: string, args: Record<string, unknown>): void {
        this.opslag.directlogAction(actie, args, 'app', 'app', true);
    }
}
